import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import Formsy from 'formsy-react';
import { Plus } from 'lucide-react';
import { DefaultTooltip, Row, Col, Button, FormBody, FormFooter, FormCard } from 'twyne-new.ui';

import { useSelectOptions } from '@Stores/base-factories/store-list';

import { useDashboardStore } from '../store';

import SortableItem from './SortableItem';
import SortableList from './SortableList';
import ManagePath from './paths/ManagePath';

const propTypes = {
  data: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default function DashboardForm({ data, onSubmit, loading }) {
  const { lists: { paths: { items, useLoad } } } = useDashboardStore()
  const [isManageVisible, setIsManageVisible] = useState(false)
  const [defaultPath, setDefaultPath] = useState({})
  const [pathPages, setPathPages] = useState([])
  const [manageData, setManageData] = useState({})
  const [manageDataIdx, setManageDataIdx] = useState(-1)
  const [managePathIdx, setManagePathIdx] = useState()
  const [target, setTarget] = useState('')
  const [profilesCollapse, setProfilesCollapse] = useState()
  const [pendingSave, setPendingSave] = useState(false)

  const pathOptions = useSelectOptions({ useLoad, items })

  const pathPagesKeyMap = useMemo(() => {
    const pathPagesMap = new Map();
    pathPages.forEach(path => {
      pathPagesMap.set(path.key, path)
    });
    return pathPagesMap
  }, [pathPages])

  useEffect(() => {
    if (data && !loading) {
      const { pathPages, defaultPath } = data;

      const pathPagesMap = new Map()

      pathPages.forEach(pathPage => pathPagesMap.set(pathPage.name, pathPage))

      const pathPrioritiesParsed = data.pathPriorities.map(name => pathPagesMap.get(name))

      setPathPages(pathPrioritiesParsed)
      setDefaultPath(defaultPath)

      !profilesCollapse && setProfilesCollapse([null, ...pathPages.map(path => path.key)])
    }
  }, [data, loading, profilesCollapse])

  const handleCloseManage = useCallback(() => {
    setManageDataIdx(-1)
    setIsManageVisible(false)
    setManageData({})
  }, [])

  const handleChangeSortable = useCallback(pathKeys => {
    handleCloseManage()
    const paths = pathKeys.map((key, index) => {
      const path = pathPagesKeyMap.get(parseInt(key, 10))
      path.sort = index;
      return path;
    });
    setPathPages(paths)
  }, [handleCloseManage, pathPagesKeyMap])

  const handleAddPath = useCallback(({ pathId, weigth }) => {
    const newPath = { id: null, path_id: pathId, weigth }

    if (manageDataIdx === -1) {
      const path = { ...defaultPath }
      path.value.push(newPath)
      setDefaultPath(path)
    } else {
      const paths = [...pathPages]
      if (!paths[manageDataIdx].value) {
        paths[manageDataIdx].value = []
      }

      paths[manageDataIdx].value.push(newPath)
      setPathPages(paths)
    }
    setPendingSave(true)
  }, [manageDataIdx, pathPages, defaultPath])

  const handleRemovePath = useCallback(() => {
    if (manageDataIdx === -1) {
      const paths = { ...defaultPath }
      paths.value.splice(managePathIdx, 1)
      setDefaultPath(paths)
    } else {
      const paths = [...pathPages]
      paths[manageDataIdx].value.splice(managePathIdx, 1)
      setPathPages(paths)
    }
    handleCloseManage()
    setPendingSave(true)
  }, [manageDataIdx, pathPages, managePathIdx, defaultPath, handleCloseManage])

  const handleEditPath = useCallback(({ pathId, weigth }) => {
    if (manageDataIdx === -1) {
      const paths = { ...defaultPath }
      paths.value[managePathIdx] = { ...paths.value[managePathIdx], path_id: pathId, weigth }
      setDefaultPath(paths)
    } else {
      const paths = [...pathPages]
      paths[manageDataIdx].value[managePathIdx] = { ...paths[manageDataIdx].value[managePathIdx], path_id: pathId, weigth }
      setPathPages(paths)
    }
    setPendingSave(true)
  }, [manageDataIdx, pathPages, defaultPath, managePathIdx])

  const handleToggleManage = useCallback((profileIndex, pathIndex) => {
    const path = profileIndex === -1 ? { ...defaultPath } : { ...pathPages[profileIndex] }
    setIsManageVisible(true)
    setManageData(path)
    setManageDataIdx(profileIndex)
    setManagePathIdx(pathIndex)

    if (pathIndex >= 0) {
      const pathEdit = path.value[pathIndex]
      setTarget(`progress-${pathEdit.id}-${pathEdit.path_id}-${pathIndex}`)
      return
    }

    setTarget(`add-profile-${path.key}`)
  }, [pathPages, defaultPath])

  const handleClickSubmit = () => {
    handleCloseManage()
    onSubmit({ defaultPath, pathPages, siteId: data.id })
    setPendingSave(false)
  }

  const handleChangeCollapse = key => {
    const isCollapse = profilesCollapse.includes(key)
    let collapses = [...profilesCollapse]
    const methods = {
      add: () => [...collapses, key],
      remove: () => collapses.filter(item => item !== key),
    }

    collapses = isCollapse ? methods.remove() : methods.add()
    setProfilesCollapse(collapses)
  }

  const optionsMapping = useMemo(() => {
    const optionsMap = new Map();
    pathOptions.forEach(option => {
      optionsMap.set(option.value, option.label)
    });
    return optionsMap
  }, [pathOptions])

  const getLabel = value => {
    const label = optionsMapping.get(value);
    return label ? label : 'n/a';
  }

  const AddUserProfileButton = () => <DefaultTooltip side='left' content={pendingSave && 'Save user profiles before editing them'} triggerProps={{ asChild: true }}>
    <Link to={!pendingSave && `/path/dashboard/user_profile/add`}>
      <Button
        color="accent"
        disabled={pendingSave}
      >
        <Plus size={14} />
        Add user profile
      </Button>
    </Link>
  </DefaultTooltip>

  return <Formsy onValidSubmit={handleClickSubmit}>
    <FormBody className='h-[calc(100vh-112px)]'>
      <Row>
        <Col>
          <FormCard title='Default'>
            <ul className="list-group">
              <SortableItem
                index={-1}
                item={defaultPath}
                options={pathOptions}
                onToggleManage={handleToggleManage}
                isCollapse={profilesCollapse && profilesCollapse.includes(null)}
                onChangeCollapse={handleChangeCollapse}
                getLabel={getLabel}
              />
            </ul>
          </FormCard>
        </Col>
        <Col>
          <FormCard title='Path Priorities' headerButtons={<AddUserProfileButton />}>
            <SortableList
              items={pathPages}
              options={pathOptions}
              onChangeSortable={handleChangeSortable}
              onToggleManage={handleToggleManage}
              profilesCollapse={profilesCollapse}
              onChangeCollapse={handleChangeCollapse}
              pendingSave={pendingSave}
              getLabel={getLabel}
            />
          </FormCard>
        </Col>
      </Row>
      {isManageVisible && <ManagePath
        getLabel={getLabel}
        target={target}
        isVisible={isManageVisible}
        onClose={handleCloseManage}
        options={pathOptions}
        onAdd={handleAddPath}
        onRemove={handleRemovePath}
        onEdit={handleEditPath}
        values={manageData.value && manageData.value[managePathIdx]}
      />}
      <FormFooter>
        <Button type="submit" color="accent">Save</Button>
      </FormFooter>
    </FormBody>
  </Formsy>
}

DashboardForm.propTypes = propTypes
DashboardForm.defaultProps = {}
